import React, { useState } from 'react';
import { calculateAge, calculateDistance } from '../../utils/profileUtils';
import { Slide } from 'react-slideshow-image';
import 'react-slideshow-image/dist/styles.css';
import './ProfileDetail.css';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
// Mapping des intérêts avec leurs icônes Font Awesome
const interestIcons = {
  'Sport': 'fas fa-running',
  'Musique': 'fas fa-music',
  'Voyage': 'fas fa-plane',
  'Lecture': 'fas fa-book',
  'Cinéma': 'fas fa-film',
  'Art': 'fas fa-palette',
  'Photographie': 'fas fa-camera',
  'Cuisine': 'fas fa-utensils',
  'Danse': 'fas fa-dancing',
  'Gaming': 'fas fa-gamepad',
  'Mode': 'fas fa-tshirt',
  'Technologie': 'fas fa-microchip',
  'Nature': 'fas fa-leaf',
  'Fitness': 'fas fa-dumbbell',
  'Yoga': 'fas fa-pray',
  'Méditation': 'fas fa-om',
  'Théâtre': 'fas fa-theater-masks',
  'Concert': 'fas fa-guitar',
  'Festival': 'fas fa-star',
  'Randonnée': 'fas fa-hiking',
  'Plage': 'fas fa-umbrella-beach',
  'Shopping': 'fas fa-shopping-bag',
  'Bénévolat': 'fas fa-hands-helping',
  'Langues': 'fas fa-language'
};

const ProfileDetail = ({ user, userLocation, onClose }) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(null);
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match this with CSS animation duration
  };

  if (!user) return null;

  const distance = calculateDistance(userLocation, user.location);
  const distanceText = distance ? t('profile.profileDetail.distance', { distance }) : t('profile.profileDetail.nearbyDistance');
  const age = calculateAge(user.doB);
  const photos = user.photos?.filter((photo) => photo?.photoUrl) || [];

  const getInterestItems = () => {
    if (!user.interests) return [];

    const interests = typeof user.interests === 'string'
      ? user.interests.split(',').map((interest) => interest.trim()).filter(Boolean)
      : user.interests;

    return interests.map((interest) => {
      const interestName = typeof interest === 'string' ? interest : interest.name;
      if (!interestName) return null;

      const matchingInterest = Object.keys(interestIcons).find(
        key => key.toLowerCase() === interestName.toLowerCase()
      );

      return {
        name: interestName,
        icon: typeof interest === 'string'
          ? (matchingInterest ? interestIcons[matchingInterest] : 'fas fa-heart')
          : (interest.icon || (matchingInterest ? interestIcons[matchingInterest] : 'fas fa-heart'))
      };
    }).filter(Boolean);
  };

  const interestItems = getInterestItems();

  const slideProperties = {
    duration: 3000,
    transitionDuration: 500,
    infinite: true,
    indicators: true,
    arrows: true,
    pauseOnHover: true
  };
  const isPhotoViewerOpen = viewerPhotoIndex !== null;
  const activeViewerPhoto = isPhotoViewerOpen ? photos[viewerPhotoIndex] : null;
  const showPreviousPhoto = (event) => {
    event.stopPropagation();
    setViewerPhotoIndex((currentIndex) => (
      currentIndex === 0 ? photos.length - 1 : currentIndex - 1
    ));
  };
  const showNextPhoto = (event) => {
    event.stopPropagation();
    setViewerPhotoIndex((currentIndex) => (
      currentIndex === photos.length - 1 ? 0 : currentIndex + 1
    ));
  };
  const closePhotoViewer = (event) => {
    event.stopPropagation();
    setViewerPhotoIndex(null);
  };

  const content = (
    <div className={`profile-detail-overlay ${isClosing ? 'closing' : ''}`}>
      <div className="profile-detail-modal">
        <div className="detail-area">
          <div className="profile-detail-hero">
            <div className="profile-detail-media">
              <button onClick={handleClose} className="close-btn" aria-label={t('common.close', { defaultValue: 'Fermer' })}>
                <i className="fas fa-times"></i>
              </button>
              {photos.length > 0 ? (
                <Slide {...slideProperties}>
                  {photos.map((photo, index) => (
                    <div className="each-slide" key={index}>
                      <button
                        type="button"
                        className="profile-detail-photo-button"
                        onClick={() => setViewerPhotoIndex(index)}
                        aria-label={t('profile.profileDetail.photo.open', { defaultValue: 'Voir la photo en grand' })}
                      >
                        <img src={photo.photoUrl} alt={t('profile.profileDetail.photo.alt', { name: user.name, index: index + 1 })} />
                      </button>
                    </div>
                  ))}
                </Slide>
              ) : (
                <div className="profile-detail-photo-placeholder">
                  <i className="fas fa-user"></i>
                </div>
              )}
            </div>
            <div className="profile-detail-heading">
              <div>
                <h4 className="profile-detail-name">{user.name}, {age}</h4>
                <p className="profile-detail-distance">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{distanceText}</span>
                </p>
              </div>
              <div className="profile-detail-status">
                <i className="fas fa-circle"></i>
                <span>{t('profile.profileDetail.verified', { defaultValue: 'Profil' })}</span>
              </div>
            </div>
          </div>

          <div className="detail-bottom-area">
            <div className="profile-detail-summary">
              <div className="profile-detail-summary-item">
                <span className="summary-icon"><i className="fas fa-heart"></i></span>
                <div>
                  <span className="summary-label">{t('profile.profileDetail.sections.lookingFor')}</span>
                  <strong>{t('lookingFor.' + user.LookingFor, { defaultValue: user.LookingFor || '-' })}</strong>
                </div>
              </div>
              <div className="profile-detail-summary-item">
                <span className="summary-icon info"><i className="fas fa-location-arrow"></i></span>
                <div>
                  <span className="summary-label">{t('profile.profileDetail.sections.distance', { defaultValue: 'Distance' })}</span>
                  <strong>{distanceText}</strong>
                </div>
              </div>
            </div>

            <div className="profile-detail-section about">
              <h6 className="title">
                <i className="fas fa-user-circle"></i>
                {t('profile.profileDetail.sections.basicInfo')}
              </h6>
              <p className="para-text">{user.bio || t('profile.profileDetail.noBio')}</p>
            </div>

            {interestItems.length > 0 && (
              <div className="profile-detail-section interests mb-3">
                <h6 className="title">
                  <i className="fas fa-star"></i>
                  {t('profile.profileDetail.sections.interests')}
                </h6>
                <ul className="dz-tag-list">
                  {interestItems.map((interest, index) => (
                    <li key={index}>
                      <div className="dz-tag">
                        <i className={interest.icon}></i>
                        <span>{t(`interests.${interest.name.toLowerCase()}`, { defaultValue: interest.name })}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {user.languages && user.languages.length > 0 && (
              <div className="profile-detail-section languages mb-3">
                <h6 className="title">
                  <i className="fas fa-language"></i>
                  {t('languages')}
                </h6>
                <ul className="dz-tag-list">
                  {user.languages.map((language, index) => (
                    <li key={index}>
                      <div className="dz-tag">
                        <span>{t(`languages.${language.toLowerCase()}`, { defaultValue: language })}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      {isPhotoViewerOpen && activeViewerPhoto && (
        <div className="profile-photo-viewer" onClick={closePhotoViewer}>
          <button
            type="button"
            className="photo-viewer-close"
            onClick={closePhotoViewer}
            aria-label={t('common.close', { defaultValue: 'Fermer' })}
          >
            <i className="fas fa-times"></i>
          </button>
          {photos.length > 1 && (
            <button
              type="button"
              className="photo-viewer-nav previous"
              onClick={showPreviousPhoto}
              aria-label={t('common.previous', { defaultValue: 'Photo précédente' })}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}
          <img
            src={activeViewerPhoto.photoUrl}
            alt={t('profile.profileDetail.photo.alt', { name: user.name, index: viewerPhotoIndex + 1 })}
            onClick={(event) => event.stopPropagation()}
          />
          {photos.length > 1 && (
            <button
              type="button"
              className="photo-viewer-nav next"
              onClick={showNextPhoto}
              aria-label={t('common.next', { defaultValue: 'Photo suivante' })}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
          {photos.length > 1 && (
            <div className="photo-viewer-count">
              {viewerPhotoIndex + 1} / {photos.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
  return ReactDOM.createPortal(content, document.body);
};

export default ProfileDetail;
